import React, { useCallback, useEffect } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import Dictionary from '../../../models/interfaces/dictionary';
import customerService from '../../../services/customerService';
import useDebounce from '../../../hooks/useDebounce';

export const ALL_CUSTOMERS_OPTION: Dictionary = { key: null, value: 'Todos' };

type CustomerMultiSelectProps = {
  subsidiaryId: string | undefined;
  value: Dictionary[];
  // eslint-disable-next-line no-unused-vars
  onChange: (items: Dictionary[]) => void;
  label?: string;
};

export default function CustomerMultiSelect({
  subsidiaryId,
  value,
  onChange,
  label = 'Clientes'
}: CustomerMultiSelectProps) {
  const [options, setOptions] = React.useState<Dictionary[]>([ALL_CUSTOMERS_OPTION]);
  const [inputValue, setInputValue] = React.useState('');

  const debouncedFetch = useDebounce((str: string) => {
    if (!subsidiaryId) return;
    customerService.getListForAutocomplete(subsidiaryId, str).then((res) => {
      if (!res?.hits?.length) {
        setOptions([ALL_CUSTOMERS_OPTION]);
        return;
      }
      const list = res.hits.map((x: any) => ({
        key: x.objectID,
        value: x.name ?? x.businessName
      }));
      setOptions([ALL_CUSTOMERS_OPTION, ...list]);
    });
  });

  useEffect(() => {
    debouncedFetch(inputValue);
  }, [inputValue, subsidiaryId]);

  const ensureLoadedByIds = useCallback(async (companies: Dictionary[]) => {
    const missing = companies.filter(
      (company) => company.key && !options.some((option) => option.key === company.key)
    );
    if (!missing.length) return;

    const loaded = await Promise.all(
      missing.map(async (company) => {
        try {
          const customer = await customerService.getById(company.key);
          return {
            key: company.key,
            value: customer.businessName ?? customer.displayName ?? company.value
          };
        } catch {
          return company;
        }
      })
    );

    setOptions((prev) => {
      const merged = [...prev];
      loaded.forEach((item) => {
        if (!merged.some((option) => option.key === item.key)) merged.push(item);
      });
      return merged;
    });
  }, [options]);

  useEffect(() => {
    if (value.length) ensureLoadedByIds(value);
  }, [value]);

  const handleChange = (_: unknown, selected: Dictionary[]) => {
    if (!selected.length) {
      onChange([]);
      return;
    }

    const hasAll = selected.some((company) => company.key === null || company.key === '');
    if (hasAll) {
      onChange([ALL_CUSTOMERS_OPTION]);
      return;
    }

    onChange(selected.filter((company) => company.key !== null && company.key !== ''));
  };

  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={handleChange}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      getOptionLabel={(option) => option.value ?? ''}
      getOptionSelected={(option, selected) => option.key === selected.key}
      filterOptions={(items) => items}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder="Selecione..."
          fullWidth
        />
      )}
    />
  );
}

CustomerMultiSelect.defaultProps = {
  label: 'Clientes'
};

export function companiesToIds(companies: Dictionary[]): (string | null)[] {
  return companies.map((company) => {
    if (company.key === null || company.key === '') return null;
    return company.key;
  });
}

export function countSpecificCompanies(companies: Dictionary[]): number {
  return companies.filter((company) => company.key !== null && company.key !== '').length;
}

export function hasMultipleSpecificCompanies(companies: Dictionary[]): boolean {
  return countSpecificCompanies(companies) > 1;
}

export function formatSyncMessage(result: {
  created: number;
  updated: number;
  failed: { message: string }[];
}) {
  const parts: string[] = [];
  if (result.created) parts.push(`${result.created} criada(s)`);
  if (result.updated) parts.push(`${result.updated} atualizada(s)`);
  let message = parts.length ? `Tarifa ${parts.join(', ')}.` : 'Tarifa salva.';
  if (result.failed.length) {
    message += ` ${result.failed.length} falha(s): ${result.failed.map((item) => item.message).join('; ')}`;
  }
  return message;
}

export function resolveCompanySelection(result: any): {
  companies: Dictionary[];
  companyId: string | null;
} {
  if (result.company) {
    const { company } = result;
    const companyId = company.id ?? company.uid ?? null;
    const label = company.businessName ?? company.displayName ?? company.name ?? '';
    return { companies: [{ key: companyId, value: label }], companyId };
  }

  return { companies: [ALL_CUSTOMERS_OPTION], companyId: null };
}
